import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of, timer } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  groupBy,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  withLatestFrom
} from 'rxjs/operators';
import { NotificationsCommonActions } from '@capital-access/common/actions';
import {
  buildGroupedNotificationListRequest,
  NOTIFICATION_TYPE_CONFIGS,
  UNDO_DELETE_DELAY_MS
} from '../constants/notifications-drawer.constants';
import { NotificationsHubService } from '../services/notifications-hub.service';
import { NotificationsDrawerActions } from './notifications-drawer.actions';
import { selectPendingDeletes, selectRefillContext, selectTypePageStates } from './notifications-drawer.selectors';

@Injectable()
export class NotificationsDrawerEffects {
  loadNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsDrawerActions.loadNotifications),
      switchMap(() => {
        const initialLimits: Record<string, number> = {};
        for (const [code, config] of Object.entries(NOTIFICATION_TYPE_CONFIGS)) {
          initialLimits[code] = config.initialLimit;
        }
        return this.service.getGroupedNotifications(buildGroupedNotificationListRequest(initialLimits)).pipe(
          map(response => NotificationsDrawerActions.loadNotificationsSuccess({ response })),
          catchError(error => [
            NotificationsDrawerActions.loadNotificationsFailure({ error: error.message ?? 'Unknown error' })
          ])
        );
      })
    )
  );

  loadMoreForType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsDrawerActions.loadMoreForType),
      withLatestFrom(this.store.select(selectRefillContext)),
      switchMap(([{ typeCode, refillCount }, { categories }]) => {
        const config = NOTIFICATION_TYPE_CONFIGS[typeCode];
        if (!config) {
          return [
            NotificationsDrawerActions.loadMoreForTypeFailure({
              typeCode,
              error: `Unknown notification type: ${typeCode}`
            })
          ];
        }

        const loadedCount =
          categories.flatMap(cat => cat.notificationsByType).find(nt => nt.notificationTypeCode === typeCode)
            ?.notifications.length ?? 0;

        if (refillCount != null) {
          const pageSize = config.loadMoreLimit ?? config.initialLimit; // 20
          const pageNumber = Math.floor(loadedCount / pageSize) + 1; // Correctly calculates next page
          const overlap = loadedCount % pageSize;

          return this.service
            .getNotificationsByType(config.notificationTypeId, config.withinDays, pageNumber, pageSize)
            .pipe(
              map(response => {
                const newNotifications = response.notifications.slice(overlap);
                return NotificationsDrawerActions.loadMoreForTypeSuccess({
                  typeCode,
                  response: {
                    notifications: newNotifications,
                    totalCount: response.totalCount,
                    unreadCount: response.unreadCount
                  }
                });
              }),
              catchError(error => [
                NotificationsDrawerActions.loadMoreForTypeFailure({
                  typeCode,
                  error: error.message ?? 'Unknown error'
                })
              ])
            );
        }

        const pageSize = config.loadMoreLimit ?? config.initialLimit;
        const firstPageNum = Math.floor(loadedCount / pageSize) + 1;
        const overlap = loadedCount % pageSize;

        return this.service
          .getNotificationsByType(config.notificationTypeId, config.withinDays, firstPageNum, pageSize)
          .pipe(
            map(firstResponse => {
              // Remove items at the front of the page that are already in state (overlap).
              const newNotifications = firstResponse.notifications.slice(overlap);
              return NotificationsDrawerActions.loadMoreForTypeSuccess({
                typeCode,
                response: {
                  notifications: newNotifications,
                  totalCount: firstResponse.totalCount,
                  unreadCount: firstResponse.unreadCount
                }
              });
            }),
            catchError(error => [
              NotificationsDrawerActions.loadMoreForTypeFailure({
                typeCode,
                error: error.message ?? 'Unknown error'
              })
            ])
          );
      })
    )
  );

  /**
   * After a successful delete, if the type's locally-loaded buffer is now
   * shorter than the server total, fetch exactly 1 item to refill the visible
   * list without consuming extra quota from the user's next Show More click.
   */
  refillAfterDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsDrawerActions.deleteNotificationSuccess),
      withLatestFrom(this.store.select(selectRefillContext)),
      filter(([{ typeCode }, { typePageStates }]) => !!typePageStates[typeCode]?.needsRefill),
      map(([{ typeCode }]) => NotificationsDrawerActions.loadMoreForType({ typeCode, refillCount: 1 }))
    )
  );

  markAsRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsDrawerActions.markAsRead),
      mergeMap(({ notificationId }) =>
        this.service.markAsRead(notificationId).pipe(
          map(() => NotificationsDrawerActions.markAsReadSuccess({ notificationId })),
          catchError(() => EMPTY)
        )
      )
    )
  );

  softDeleteNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsDrawerActions.softDeleteNotification),
      mergeMap(({ notificationId, typeCode }) =>
        // Read wasUnread immediately (before the timer) so the value is captured
        // at the moment of the soft-delete, not 3 s later when the entry may
        // have already been removed from pendingDeletes by an undo action.
        this.store.select(selectPendingDeletes).pipe(
          take(1),
          mergeMap(pendingDeletes => {
            const wasUnread = pendingDeletes[notificationId]?.wasUnread ?? false;
            return timer(UNDO_DELETE_DELAY_MS).pipe(
              map(() => NotificationsDrawerActions.deleteNotification({ notificationId, typeCode, wasUnread })),
              takeUntil(
                this.actions$.pipe(
                  ofType(NotificationsDrawerActions.undoDelete),
                  filter(a => a.notificationId === notificationId)
                )
              )
            );
          })
        )
      )
    )
  );

  deleteNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsDrawerActions.deleteNotification),
      groupBy(({ notificationId }) => notificationId),
      mergeMap(group$ =>
        group$.pipe(
          exhaustMap(action =>
            of(action).pipe(
              withLatestFrom(this.store.select(selectRefillContext)),
              mergeMap(([{ notificationId, typeCode, wasUnread: actionWasUnread }, { categories }]) => {
                const notification = categories
                  .flatMap(cat => cat.notificationsByType)
                  .find(nt => nt.notificationTypeCode === typeCode)
                  ?.notifications.find(n => n.notificationId === notificationId);
                const wasUnread = notification ? !notification.read : actionWasUnread ?? false;
                return this.service.deleteNotification(notificationId).pipe(
                  map(() =>
                    NotificationsDrawerActions.deleteNotificationSuccess({ notificationId, typeCode, wasUnread })
                  ),
                  catchError(() =>
                    of(NotificationsDrawerActions.deleteNotificationFailure({ notificationId, typeCode }))
                  )
                );
              })
            )
          )
        )
      )
    )
  );

  refreshDrawer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsCommonActions.refreshNotificationsDrawer),
      map(() => NotificationsDrawerActions.loadNotifications())
    )
  );

  constructor(private actions$: Actions, private store: Store, private service: NotificationsHubService) {}
}
